import React from 'react'
import { useSyncedStore } from '@syncedstore/react';

import Container from 'src/components/shared/container';
import WorkerCard from 'src/components/shared/cards/worker';
import Fallback from 'src/components/shared/fallback';
import { addWorkerOut, revertWorkerOut } from 'src/store/todo/operations';
import { todoStore } from 'src/store/todo';
import { checkForDuplicates } from 'src/utils/duplicates';

type FilterType = 'all' | 'out' | 'in'

type Props={
    teamId:string
    filter: FilterType
    isTimeclockMode: boolean
}

const TeamDetails: React.FC<Props> = ({teamId, filter, isTimeclockMode}) => {
    const state = useSyncedStore(todoStore);

    const workers = state.workers?.filter(w => w.teamId === teamId && w.isActive) || [];

    // Check for duplicates and warn
    React.useEffect(() => {
        checkForDuplicates(workers, 'Workers')
    }, [workers.length])

    const getIsWorkerOut = (workerId:string) => {
      const workerOut = state.workerOuts.find(w =>
          w.workerId === workerId && !w.softDeleted
      );
      return !!workerOut;
    }

    const getWorkerOutId = (workerId: string) => {
      const workerOut = state.workerOuts.find(w =>
          w.workerId === workerId && !w.softDeleted
      );
      return workerOut?.id;
    }

    const getIsWorkerAssigned = (workerId: string) => {
      const assignment = state.workerAssignments.find(a =>
          a.workerId === workerId && !a.softDeleted
      );
      return !!assignment;
    }

    const filteredWorkers = workers.filter((w) => {
        const isWorkerOut = getIsWorkerOut(w.id);
        const isAssigned = getIsWorkerAssigned(w.id);

        // Apply filter first
        if (filter === 'out') {
            return isWorkerOut;
        }

        if (filter === 'in') {
            // In timeclock mode, "In" means assigned workers who are not out
            if (isTimeclockMode) {
                return !isWorkerOut && isAssigned;
            }
            // Otherwise, just workers who are not out
            return !isWorkerOut;
        }

        // Filter is 'all'
        if (isTimeclockMode) {
            // In timeclock mode, show all assigned workers (whether in or out)
            return isAssigned;
        }

        return true; // Show all workers
    });

    return (
        <Container>
            <div className='flex flex-col gap-2 w-full pt pb-20'>
                 {filteredWorkers.length > 0 ?
                (filteredWorkers.map((w) => {
                   const isWorkerOut = getIsWorkerOut(w.id);
                   const workerOutId = getWorkerOutId(w.id);
                   const isAssigned = getIsWorkerAssigned(w.id);
                   // Status priority: Out > Assigned > Unassigned
                   // Note: A worker can be both Out AND have assignments
                   // When they go "Out", assignments are preserved but hidden
                   // When they revert from "Out", assignments reappear
                   const workerStatus = isWorkerOut ? 'Out' : (isAssigned ? 'Assigned' : 'Unassigned');
                    return (
                    <WorkerCard
                    key={w.id}
                    first_name={w.firstName}
                    last_name={w.lastName}
                    status={workerStatus}
                    role=""
                    workerId={w.id}
                    teamId={teamId}
                    onWorkerOut={() =>  addWorkerOut(teamId, w.id)}
                    onRevert={() => workerOutId && revertWorkerOut(workerOutId)}
                    isTimeclockMode={isTimeclockMode}
                />
                )
                })): <Fallback title='No worker found!'/>
                }
            </div>
        </Container>
    )
}

export { type FilterType };
export default TeamDetails
